import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FeatureFlags } from 'components/common/types/Features.types';
import { getDeal } from 'modules/api/programmaticDeal';
import { CommonDeal, FrontEndType, Programmatic } from 'components/common/types/Deal.types';
import { BackupFormData } from 'store/dealManagement/types';
import { changeDealCurrentLineData, duplicateDeal, toggleCampaignDrawerOpen } from 'store/dealManagement/reducer';
import { transformDealLineDetails } from 'components/pages/DealWithLines/DealLines/transformDealDetails';
import { addToastNotification } from 'store/notification/reducer';
import { cancelFunctions } from 'utils/cancelFunctions';
import { useHasFeatureAccess } from './useHasFeatureAccess';

type DuplicateDealHandler = (dealId: string) => Promise<void>;

export interface UseDuplicateDealReturn {
  handleDuplicateDeal: DuplicateDealHandler;
}

export const useDuplicateDeal = (): UseDuplicateDealReturn => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hasNonGuaranteedExtendedTargetEnabled = useHasFeatureAccess(FeatureFlags.NON_GUARANTEED_EXTENDED_TARGET);
  const hasAdsDealLevelCPMEnabled = useHasFeatureAccess(FeatureFlags.DEAL_LEVEL_CPM);
  const hasOnlyAllowPlanner = useHasFeatureAccess(FeatureFlags.ONLY_ALLOW_PLANNER);

  const handleDuplicateDeal: DuplicateDealHandler = async (dealId) => {
    try {
      const dealDetails = await getDeal(dealId, cancelFunctions);
      const duplicatedDealDetails = {
        ...{
          ...dealDetails,
          dspCode: null,
          dspName: null,
        },
        dealLines: [{ ...dealDetails.dealLines[0], creativeType: null }],
      };

      dispatch(
        duplicateDeal({
          ...(transformDealLineDetails({
            ...duplicatedDealDetails,
            hasNonGuaranteedExtendedTargetEnabled,
          }) as {
            backupFormData: BackupFormData;
            commonDeal: CommonDeal;
            programmatic: Programmatic;
          }),
          hasAdsDealLevelCPMEnabled,
        }),
      );
      dispatch(
        changeDealCurrentLineData({
          endDate: new Date(new Date().setHours(23, 59, 59, 999)),
          startDate: new Date(new Date().setHours(0, 0, 0, 0)),
        }),
      );

      dispatch(toggleCampaignDrawerOpen(true));

      if (hasOnlyAllowPlanner || dealDetails.dealLines[0].frontEndType === FrontEndType.PLANNER) {
        navigate('/planner/programmatic');
      } else {
        navigate('/deal');
      }

      dispatch(
        addToastNotification({
          description: `${dealDetails.name} campaign has been duplicated`,
          timeout: 2000,
          type: 'Success',
        }),
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error duplicating deal:', error);
    }
  };

  return {
    handleDuplicateDeal,
  };
};
